import { Component, ReactElement } from 'react';
import { ANIMATION_DURATION, handleRefSplit } from '@optimus-web/common';
import { noop } from '../utils/func';
import { CalendarService } from '../utils/services/createCalendarService';
import { CalendarMonthProps } from './CalendarMonth';
import { MONTH_WIDTH, MonthGridElement } from './style';

type CalendarMonthGridAnimatorChildren<D, M, Y> = ReactElement<CalendarMonthProps<D, M, Y>>[];

interface CalendarMonthGridAnimatorProps<D, M, Y> {
    children: CalendarMonthGridAnimatorChildren<D, M, Y>;
    getCalendarService: () => CalendarService<D, M, Y>;
    innerRef?: React.Ref<HTMLElement | null>;
    isMobile?: boolean;
}

interface CalendarMonthGridAnimatorState<D, M, Y> {
    children: CalendarMonthGridAnimatorChildren<D, M, Y>;
    nextChildren?: CalendarMonthGridAnimatorChildren<D, M, Y> | null;
}

export class CalendarMonthGridAnimator<D, M, Y> extends Component<
    CalendarMonthGridAnimatorProps<D, M, Y>,
    CalendarMonthGridAnimatorState<D, M, Y>
> {
    private transitionTimeout?: number | null;
    private monthGridElement?: HTMLElement | null;

    constructor(props: CalendarMonthGridAnimatorProps<D, M, Y>) {
        super(props);

        this.state = {
            children: props.children,
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps: CalendarMonthGridAnimatorProps<D, M, Y>) {
        const prev = this.state.nextChildren || this.state.children;
        this.mergeMonths(prev, nextProps.children);
    }

    componentWillUnmount() {
        if (this.transitionTimeout) {
            window.clearTimeout(this.transitionTimeout);
            this.transitionTimeout = null;
        }
    }

    handleMonthGridElementRef = (c: HTMLElement | null) => (this.monthGridElement = c);

    mergeMonths = (
        prev: CalendarMonthGridAnimatorChildren<D, M, Y>,
        next: CalendarMonthGridAnimatorChildren<D, M, Y>
    ): void => {
        // cancel previous transition and restart with new state
        // TODO condition by month change only?
        if (this.transitionTimeout) {
            return this.flushTransition(() => this.mergeMonths(this.state.children, next));
        }

        const { getCalendarService } = this.props;

        const prevMonth = prev[0].props.month;
        const nextMonth = next[0].props.month;

        // first replace any currently displayed months
        let hasChildrenChanged;
        const updatedChildren = [...prev];
        next.forEach((n) => {
            const prevIndex = updatedChildren.findIndex((p) => p.key === n.key);
            if (prevIndex !== -1) {
                hasChildrenChanged = true;
                updatedChildren[prevIndex] = n;
            }
        });

        // shorten the months
        if (next.length < prev.length) {
            updatedChildren.length = next.length;
            hasChildrenChanged = true;
            // extend the months
        } else if (next.length > prev.length) {
            updatedChildren.push(...next.slice(prev.length));
            hasChildrenChanged = true;
        }

        const hasMonthChanged = !getCalendarService().isMonthEqual(prevMonth, nextMonth);
        if (hasMonthChanged) {
            // compute number of months to be slided in
            const numberOfMonths = updatedChildren.length;
            const newMonthsCount = Math.min(
                Math.abs(getCalendarService().diffMonths(nextMonth, prevMonth)),
                numberOfMonths
            );

            let children;
            let startTransition;
            // sliding to right
            if (getCalendarService().isMonthGreater(nextMonth, prevMonth)) {
                startTransition = this.startNextTransition;
                children = [...updatedChildren, ...next.slice(-newMonthsCount)];
                // sliding to left
            } else {
                startTransition = this.startPrevTransition;
                children = [...next.slice(0, newMonthsCount), ...updatedChildren];
            }

            this.setState({ children, nextChildren: next }, startTransition);
        } else if (hasChildrenChanged) {
            this.setState({ children: updatedChildren });
        }
    };

    startNextTransition = () => {
        const { children, nextChildren } = this.state;
        const { isMobile } = this.props;

        if (nextChildren) {
            const newMonthsCount = children.length - nextChildren.length;

            const transform = `translateX(-${isMobile ? '100%' : `${newMonthsCount * MONTH_WIDTH}px`})`;
            this.startTransition(transform);
        }
    };

    startPrevTransition = () => {
        const { children, nextChildren } = this.state;
        const { isMobile } = this.props;

        if (this.monthGridElement && nextChildren) {
            const newMonthsCount = children.length - nextChildren.length;

            // prepare transition from left by shifting currently visible months
            const initialTransform = `translateX(-${isMobile ? '100%' : `${newMonthsCount * MONTH_WIDTH}px`})`;
            this.monthGridElement.style.transform = initialTransform;
            // force repaint
            this.monthGridElement.offsetHeight; // eslint-disable-line no-unused-expressions

            const transform = 'translateX(0)';
            this.startTransition(transform);
        }
    };

    startTransition(transform: string) {
        if (this.monthGridElement) {
            this.monthGridElement.style.transition = `${ANIMATION_DURATION.fast}ms transform`;
            this.monthGridElement.style.transform = transform;
            // force repaint
            this.monthGridElement.offsetHeight; // eslint-disable-line no-unused-expressions

            this.transitionTimeout = window.setTimeout(this.finishTransition, ANIMATION_DURATION.fast);
        }
    }

    flushTransition = (callback: (...args: any[]) => void) => {
        if (this.transitionTimeout) {
            window.clearTimeout(this.transitionTimeout);
            this.transitionTimeout = null;

            this.finishTransition(callback);
        }
    };

    finishTransition = (callback = noop) => {
        this.transitionTimeout = null;

        if (this.monthGridElement) {
            this.monthGridElement.style.transition = '';
            this.monthGridElement.style.transform = '';
            // force repaint
            this.monthGridElement.offsetHeight; // eslint-disable-line no-unused-expressions
        }

        if (this.state.nextChildren) {
            this.setState(
                (state) =>
                    state.nextChildren
                        ? {
                              children: state.nextChildren,
                              nextChildren: null,
                          }
                        : null,
                callback
            );
        }
    };

    render() {
        const { innerRef, isMobile, ...other } = this.props;
        const { children } = this.state;

        const style = {
            width: `${isMobile ? '100%' : `${children.length * MONTH_WIDTH}px`}`,
        };

        const monthGridProps = {
            ref: handleRefSplit({ ownRef: this.handleMonthGridElementRef, forwardedRef: innerRef }),
            style,
            ...other,
        };

        return <MonthGridElement {...monthGridProps}>{children}</MonthGridElement>;
    }
}

export default CalendarMonthGridAnimator;
